import { SlidersHorizontal } from "lucide-react";

const FIELDS = {
  "ftir-spectrum": [
    { key: "resolution", label: "Resolution (cm⁻¹)", placeholder: "e.g. 4" },
    { key: "scans", label: "Scans", placeholder: "e.g. 32" },
    { key: "range", label: "Range (cm⁻¹)", placeholder: "auto from data" },
    { key: "technique", label: "Technique", options: ["ATR", "KBr pellet", "Thin film", "Nujol mull", "Diffuse reflectance (DRIFT)"] },
    { key: "mode", label: "Mode", options: ["Transmittance (%T)", "Absorbance (A)"] },
  ],
  "hplc-chromatogram": [
    { key: "column", label: "Column", placeholder: "e.g. C18, 150 × 4.6 mm, 5 µm" },
    { key: "mobile_phase", label: "Mobile phase", placeholder: "e.g. ACN:H₂O 60:40" },
    { key: "flow_rate", label: "Flow rate (mL/min)", placeholder: "e.g. 1.0" },
    { key: "wavelength", label: "λ (nm)", placeholder: "e.g. 254" },
    { key: "injection_volume", label: "Vinj (µL)", placeholder: "e.g. 10" },
  ],
};

export const hasInstrumentFields = (analysisType) => Boolean(FIELDS[analysisType]);

export const InstrumentParamsFields = ({ analysisType, value, onChange, dark = false }) => {
  const fields = FIELDS[analysisType];
  if (!fields) return null;
  const set = (k, v) => onChange({ ...value, [k]: v });
  const inputCls = dark
    ? "w-full rounded-md px-2.5 py-1.5 text-xs bg-transparent border border-[var(--border)] text-[var(--text)] focus:outline-none focus:border-[var(--teal)] transition-colors"
    : "w-full rounded-md px-3 py-2 text-sm border border-slate-300 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow";
  const labelCls = dark ? "block text-[10px] uppercase tracking-wider mb-1 text-[var(--text3)]" : "block text-xs font-semibold text-slate-600 mb-1";

  return (
    <div data-testid="instrument-params-fields">
      <div className={dark ? "demo-panel-label" : "block text-sm font-semibold text-slate-700 mb-1"}>
        Instrumental parameters <span style={{ fontWeight: 400, opacity: 0.7 }}>(optional)</span>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-2">
        {fields.map((f) => (
          <label key={f.key} className={f.key === "column" || f.key === "mobile_phase" ? "col-span-2" : ""}>
            <span className={labelCls}>{f.label}</span>
            {f.options ? (
              <select
                data-testid={`instrument-param-${f.key}`}
                className={inputCls}
                value={value[f.key] || ""}
                onChange={(e) => set(f.key, e.target.value)}
              >
                <option value="">— Not specified</option>
                {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <input
                data-testid={`instrument-param-${f.key}`}
                className={inputCls}
                value={value[f.key] || ""}
                placeholder={f.placeholder}
                onChange={(e) => set(f.key, e.target.value)}
              />
            )}
          </label>
        ))}
      </div>
    </div>
  );
};

export const InstrumentParamsBox = ({ rows }) => {
  if (!rows?.length) return null;
  return (
    <div className="demo-result-block" data-testid="instrument-params-box">
      <div className="demo-section-h blue">
        <SlidersHorizontal size={11} /> Instrumental Parameters
      </div>
      <div
        style={{
          display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(150px, 1fr))`, gap: 10,
          padding: "12px 14px", borderRadius: 6,
          border: "1px solid var(--border, rgba(255,255,255,0.08))",
          background: "var(--surface2, rgba(255,255,255,0.02))",
        }}
      >
        {rows.map((r) => {
          const missing = r.value?.startsWith("— Not specified");
          return (
            <div key={r.key} data-testid={`instrument-param-value-${r.key}`}>
              <div style={{ fontSize: 10, color: "var(--text3)", fontFamily: "var(--mono)", textTransform: "uppercase", letterSpacing: 0.5 }}>
                {r.label}
              </div>
              <div style={{ fontSize: 12.5, marginTop: 4, fontFamily: "var(--mono)", color: missing ? "var(--text3)" : "var(--text)", fontStyle: missing ? "italic" : "normal" }}>
                {r.value}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
